const FLAVOR_OPTION_NAMES = ['Flavor', 'POSSIBLE® Snack Bar (Flavor)', 'POSSIBLE® Protein Powder (Flavor)'];

var StickyAtcBar = class extends HTMLElement {
  connectedCallback() {
    const formId = this.dataset.productFormId;

    this.form = document.getElementById(formId);
    if (!this.form) return;

    this.variantInput = this.form.querySelector('input[name="id"]');
    this.mainSubmitButton = this.form.querySelector('.product-form__submit');

    this.isProteinPowder = this.dataset.isProteinPowder === 'true';
    this.hasSubscription = this.dataset.hasSubscription === 'true';

    this.variantPricing = this.parseJson('[data-sticky-variant-pricing]') || [];
    this.optionNames = this.parseJson('[data-sticky-option-names]') || [];

    this.imageEl = this.querySelector('[data-sticky-image]');
    this.variantNameEl = this.querySelector('[data-variant-name]');
    this.servingEl = this.querySelector('[data-serving-suffix]');
    this.priceEl = this.querySelector('[data-price-current]');
    this.priceCompareEl = this.querySelector('[data-price-compare]');
    this.savingsBadgeEl = this.querySelector('[data-savings-badge]');
    this.ctaTextEl = this.querySelector('[data-cta-text]');
    this.ctaButton = this.querySelector('[data-sticky-submit]');
    this.ctaSpinner = this.ctaButton.querySelector('.loading-overlay__spinner');
    this.tabsRow = this.querySelector('[data-tabs-row]');
    this.tabs = this.querySelectorAll('.sticky-atc-bar__tab');

    this.purchaseType = 'onetime';

    this.bindEvents();
    this.observeBuyBox();
    this.observeMainButtonLoading();

    if (!this.hasSubscription) this.init();
  }

  disconnectedCallback() {
    if (this.buyBoxObserver) this.buyBoxObserver.disconnect();
    if (this.mainButtonObserver) this.mainButtonObserver.disconnect();
  }

  init() {
    this.render();
    this.removeAttribute('hidden');
    this.updateStickyHeightVar(this.classList.contains('is-visible'));
  }

  // Publishes the bar's current height as a CSS custom property on <body>.
  // Third-party overlays (Shopify Chat, Klaviyo teaser) consume it via the
  // rules in sticky-atc-bar.css, so we don't need to query them from JS.
  updateStickyHeightVar(visible) {
    const value = visible ? this.offsetHeight + 'px' : '0px';
    document.body.style.setProperty('--sticky-atc-height', value);
  }

  parseJson(selector) {
    const el = this.querySelector(selector);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      console.error('sticky-atc-bar: invalid JSON in', selector, e);
      return null;
    }
  }

  bindEvents() {
    document.addEventListener('variant:change', () => this.render());

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => this.setPurchaseType(tab.dataset.purchaseType));
    });

    this.ctaButton.addEventListener('click', (e) => {
      e.preventDefault();
      if (this.ctaButton.hasAttribute('disabled')) return;
      if (this.mainSubmitButton) this.mainSubmitButton.click();
    });

    if (this.hasSubscription) this.bindRechargeListener();
  }

  bindRechargeListener(attempts = 0) {
    const rechargeWidget = this.getRechargeWidget();
    if (!rechargeWidget) {
      if (attempts < 25) {
        setTimeout(() => this.bindRechargeListener(attempts + 1), 200);
      } else {
        this.init();
      }
      return;
    }
    this.rechargeWidget = rechargeWidget;

    const readCurrentType = () => {
      const selected = rechargeWidget.root.querySelector(rechargeWidget.inputs + ':checked');
      if (!selected) return null;
      return selected.value === 'onetime' ? 'onetime' : 'subscription';
    };

    const syncFromRecharge = () => {
      const next = readCurrentType();
      if (!next || next === this.purchaseType) return;
      this.purchaseType = next;
      this.renderTabs();
      this.renderPriceAndCta();
    };

    rechargeWidget.root.addEventListener('change', syncFromRecharge);
    rechargeWidget.root.addEventListener('input', syncFromRecharge);

    this.waitForInitialRechargeState(readCurrentType);
  }

  // The widget element can appear in the DOM before Recharge sets the initial
  // :checked state on its radios. Poll briefly so our first render reflects
  // Recharge's actual default selection rather than the sticky bar's fallback.
  waitForInitialRechargeState(readCurrentType, attempts = 0) {
    const initial = readCurrentType();
    if (initial) {
      this.purchaseType = initial;
      this.init();
      return;
    }
    if (attempts < 25) {
      setTimeout(() => this.waitForInitialRechargeState(readCurrentType, attempts + 1), 200);
    } else {
      this.init();
    }
  }

  // Resolves which Recharge widget shape is on the page. Newer Recharge
  // implementations use a shadow root on <recharge-subscription-widget> with
  // .rc-purchase-option__subscription / __onetime markup. The current
  // implementation here uses light-DOM markup with [data-selector-*] hooks.
  getRechargeWidget() {
    const host = document.querySelector('recharge-subscription-widget, [data-widget-container-wrapper]');
    if (host && host.shadowRoot) {
      return {
        root: host.shadowRoot,
        inputs: 'input[name="purchaseOption"]',
        subscribeLabel: 'div.rc-purchase-option.rc-purchase-option__subscription > label',
        onetimeLabel: 'div.rc-purchase-option.rc-selection__onetime > label',
      };
    }
    const lightWidget = document.querySelector('[data-widget-container-wrapper]');
    if (lightWidget) {
      return {
        root: lightWidget,
        inputs: '.rc_widget__option__input',
        subscribeLabel: '[data-selector-subsave] > label',
        onetimeLabel: '[data-selector-onetime] > label',
      };
    }
    return null;
  }

  observeBuyBox() {
    const buyBox = this.form.querySelector('.product-form__buttons') || this.mainSubmitButton;
    if (!buyBox) return;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        let visible;
        if (entry.isIntersecting) {
          visible = false;
        } else {
          visible = entry.boundingClientRect.top < 0;
        }
        this.classList.toggle('is-visible', visible);
        this.updateStickyHeightVar(visible);
      });
    }, { threshold: 0 });
    observer.observe(buyBox);
    this.buyBoxObserver = observer;
  }

  observeMainButtonLoading() {
    if (!this.mainSubmitButton) return;
    const observer = new MutationObserver(() => {
      const isLoading = this.mainSubmitButton.classList.contains('loading');
      this.ctaButton.classList.toggle('loading', isLoading);
      this.ctaSpinner.classList.toggle('hidden', !isLoading);
      if (this.mainSubmitButton.hasAttribute('disabled')) {
        this.ctaButton.setAttribute('disabled', '');
      } else if (!this.variantUnavailable) {
        this.ctaButton.removeAttribute('disabled');
      }
    });
    observer.observe(this.mainSubmitButton, { attributes: true, attributeFilter: ['class', 'disabled'] });
    this.mainButtonObserver = observer;
  }

  currentVariantData() {
    if (!this.variantInput) return null;
    const id = parseInt(this.variantInput.value, 10);
    return this.variantPricing.find((v) => v.id === id) || null;
  }

  setPurchaseType(type) {
    if (type === this.purchaseType) return;
    this.purchaseType = type;
    this.renderTabs();
    this.renderPriceAndCta();
    this.syncToRecharge(type);
  }

  syncToRecharge(type) {
    const rechargeWidget = this.rechargeWidget || this.getRechargeWidget();
    if (!rechargeWidget) return;
    const selector = type === 'subscription' ? rechargeWidget.subscribeLabel : rechargeWidget.onetimeLabel;
    const target = rechargeWidget.root.querySelector(selector);
    if (!target) return;
    // Focus the underlying input first with preventScroll: true. Clicking an
    // already-focused element skips the browser's "scroll focused element into
    // view" step, so we avoid the page jumping to the buy box.
    const input = target.control || target.querySelector('input') || target;
    if (input.focus) input.focus({ preventScroll: true });
    input.click();

    // Restore focus to the sticky tab that triggered the sync.
    const stickyTab = Array.from(this.tabs).find((t) => t.dataset.purchaseType === type);
    if (stickyTab) stickyTab.focus({ preventScroll: true });
  }

  render() {
    const variant = this.currentVariantData();
    if (!variant) return;

    if (this.imageEl && variant.image) {
      this.imageEl.src = variant.image;
      this.imageEl.alt = variant.title || '';
    }

    if (this.variantNameEl) this.variantNameEl.textContent = this.resolveVariantName(variant);

    if (this.servingEl) {
      const serving = this.resolveServingLabel(variant);
      this.servingEl.textContent = serving ? ' - ' + serving : '';
    }

    if (this.tabsRow) {
      const tabsShouldShow = this.hasSubscription && !!variant.selling_plan;
      this.tabsRow.hidden = !tabsShouldShow;
      if (!tabsShouldShow && this.purchaseType === 'subscription') {
        this.purchaseType = 'onetime';
        this.syncToRecharge('onetime');
      }
    }

    this.variantUnavailable = !variant.available;
    if (this.variantUnavailable) {
      this.ctaButton.setAttribute('disabled', '');
    } else if (!this.mainSubmitButton || !this.mainSubmitButton.hasAttribute('disabled')) {
      this.ctaButton.removeAttribute('disabled');
    }

    this.renderTabs();
    this.renderPriceAndCta();
  }

  renderTabs() {
    this.tabs.forEach((tab) => {
      const isActive = tab.dataset.purchaseType === this.purchaseType;
      tab.classList.toggle('is-active', isActive);
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });
  }

  renderPriceAndCta() {
    const variant = this.currentVariantData();
    if (!variant) return;

    const subSelected = this.purchaseType === 'subscription' && variant.selling_plan;

    if (subSelected) {
      this.priceEl.innerHTML = variant.selling_plan.price_formatted;
      this.priceCompareEl.innerHTML = variant.selling_plan.compare_at_price_formatted;
      this.priceCompareEl.hidden = false;
      this.savingsBadgeEl.textContent = 'Save ' + variant.selling_plan.savings_percent + '%';
      this.savingsBadgeEl.hidden = false;
      this.ctaTextEl.textContent = 'Subscribe';
    } else {
      this.priceEl.innerHTML = variant.price_formatted;
      this.priceCompareEl.hidden = true;
      this.savingsBadgeEl.hidden = true;
      this.ctaTextEl.textContent = variant.available
        ? (window.variantStrings && window.variantStrings.addToCart ? window.variantStrings.addToCart : 'Add to Cart')
        : (window.variantStrings && window.variantStrings.soldOut ? window.variantStrings.soldOut : 'Sold Out');
    }
  }

  resolveVariantName(variant) {
    const flavorIdx = this.optionNames.findIndex((name) => FLAVOR_OPTION_NAMES.includes(name));
    if (flavorIdx >= 0 && variant.options && variant.options[flavorIdx]) {
      return variant.options[flavorIdx];
    }
    return variant.title || '';
  }

  resolveServingLabel(variant) {
    if (this.isProteinPowder) {
      const servingIdx = this.optionNames.findIndex((name) => !FLAVOR_OPTION_NAMES.includes(name));
      if (servingIdx >= 0 && variant.options && variant.options[servingIdx]) {
        return variant.options[servingIdx];
      }
      return '';
    }
    return variant.quantity_label || '';
  }
}

customElements.define('sticky-atc-bar', StickyAtcBar);
